import { LEAD_ORIGIN_LABELS, type LeadOrigin } from '@agencia/shared';
import { Alert } from '@/components/ui';
import { MonthPicker } from '@/components/widgets';
import { formatCompetencia, formatPct } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { useMonth } from '@/lib/useMonth';

interface Consultor {
  id: string;
  nome: string;
  atendimentos: number;
  cotacoes: number;
  vendas: number;
  conversao: number;
  primeiraRespostaMin: number | null;
  retornosAtrasados: number;
}
interface Report {
  funil: { atendimentos: number; cotacoes: number; vendas: number; diasAteFechar: number | null };
  destaques: { maisAtendeu: Consultor | null; maisVendeu: Consultor | null; melhorConversao: Consultor | null; respondeMaisRapido: Consultor | null };
  consultores: Consultor[];
  motivosPerda: Array<{ motivo: string; total: number }>;
  origens: Array<{ origem: LeadOrigin; clientes: number; vendas: number }>;
}

const minutos = (m: number | null) => (m === null ? '—' : m < 60 ? `${Math.round(m)} min` : `${(m / 60).toFixed(1).replace('.', ',')} h`);

const Trofeu = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0z" fill="#FBBF24" />
    <path d="M7 6H4v1a3 3 0 003 3M17 6h3v1a3 3 0 01-3 3" />
  </svg>
);

/** Relatório do CRM: do primeiro atendimento à venda. */
export function CrmReportPage() {
  const [mes, setMes] = useMonth();
  const { data: r, error } = useApi<Report>(`/crm/report?competencia=${mes}`);
  const pct = (a: number, b: number) => (b ? formatPct(a / b) : '—');
  const maxPerda = Math.max(1, ...(r?.motivosPerda.map((m) => m.total) ?? [1]));
  const maxOrig = Math.max(1, ...(r?.origens.map((o) => o.clientes) ?? [1]));

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Clientes da equipe · {formatCompetencia(mes)}</h1>
          <p className="muted">Do primeiro atendimento na Digisac até a venda no Conta Azul.</p>
        </div>
        <MonthPicker value={mes} onChange={setMes} />
      </header>
      {error && <Alert>{error}</Alert>}
      {!r ? (
        !error && <p className="muted">Carregando…</p>
      ) : (
        <>
          <dl className="kpis">
            <div className="kpi kpi--navy">
              <dt>Atendimentos</dt>
              <dd>{r.funil.atendimentos}</dd>
            </div>
            <div className="kpi">
              <dt>Cotações enviadas</dt>
              <dd>{r.funil.cotacoes}</dd>
              <p className="kpi__hint">{pct(r.funil.cotacoes, r.funil.atendimentos)} dos atendimentos</p>
            </div>
            <div className="kpi kpi--orange">
              <dt>Vendas</dt>
              <dd>{r.funil.vendas}</dd>
              <p className="kpi__hint">
                {pct(r.funil.vendas, r.funil.atendimentos)} dos atendimentos · {pct(r.funil.vendas, r.funil.cotacoes)} das cotações
              </p>
            </div>
            <div className="kpi">
              <dt>Tempo até fechar</dt>
              <dd>{r.funil.diasAteFechar === null ? '—' : `${r.funil.diasAteFechar.toFixed(1).replace('.', ',')} dias`}</dd>
              <p className="kpi__hint">média do primeiro contato à venda</p>
            </div>
          </dl>

          <section className="highlights" aria-label="Destaques do mês">
            {[
              { t: 'Mais atendeu', c: r.destaques.maisAtendeu, v: (c: Consultor) => `${c.atendimentos} clientes atendidos`, trofeu: true },
              { t: 'Mais vendeu', c: r.destaques.maisVendeu, v: (c: Consultor) => `${c.vendas} vendas` },
              { t: 'Melhor conversão', c: r.destaques.melhorConversao, v: (c: Consultor) => `${formatPct(c.conversao)} dos atendimentos viraram venda` },
              { t: 'Responde mais rápido', c: r.destaques.respondeMaisRapido, v: (c: Consultor) => `primeira resposta em ${minutos(c.primeiraRespostaMin)}, em média` },
            ].map((d) => (
              <div key={d.t} className="highlight">
                <span className="highlight__label">
                  {d.trofeu && <Trofeu />}
                  {d.t}
                </span>
                <strong>{d.c?.nome ?? '—'}</strong>
                <span className="muted small">{d.c ? d.v(d.c) : 'Ainda sem dados'}</span>
              </div>
            ))}
          </section>

          <section className="card flush">
            <h2 className="pad-h">Por consultor</h2>
            {r.consultores.length === 0 ? (
              <p className="muted pad">Nenhum atendimento neste mês ainda.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Consultor</th>
                      <th scope="col" className="num">Atendimentos ↓</th>
                      <th scope="col" className="num">Cotações</th>
                      <th scope="col" className="num">Vendas</th>
                      <th scope="col">Conversão</th>
                      <th scope="col" className="num">1ª resposta</th>
                      <th scope="col" className="num">Retornos atrasados</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.consultores.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <div className="name-cell">
                            <span>{c.nome}</span>
                            {r.destaques.maisAtendeu?.id === c.id && (
                              <span className="trophy-pill">
                                <Trofeu />
                                Mais atendeu
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="num">
                          <strong>{c.atendimentos}</strong>
                        </td>
                        <td className="num">{c.cotacoes}</td>
                        <td className="num">{c.vendas}</td>
                        <td>
                          <div className="mini-bar">
                            <div style={{ width: `${Math.round(c.conversao * 100)}%` }} />
                            <span>{formatPct(c.conversao)}</span>
                          </div>
                        </td>
                        <td className="num">{minutos(c.primeiraRespostaMin)}</td>
                        <td className="num">{c.retornosAtrasados > 0 ? <span className="badge badge--warn">{c.retornosAtrasados}</span> : '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <div className="grid-2">
            <section className="card">
              <h2>Por que perdemos</h2>
              {r.motivosPerda.length === 0 ? (
                <p className="muted small">Nenhum cliente perdido neste mês.</p>
              ) : (
                <div className="bars">
                  {r.motivosPerda.map((m) => (
                    <div key={m.motivo} className="bars__row">
                      <span>{m.motivo}</span>
                      <div className="bars__track">
                        <div className="bars__fill bars__fill--orange" style={{ width: `${(m.total / maxPerda) * 100}%` }} />
                      </div>
                      <strong>{m.total}</strong>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className="card">
              <h2>De onde vêm os clientes</h2>
              {r.origens.length === 0 ? (
                <p className="muted small">Sem atendimentos neste mês.</p>
              ) : (
                <div className="bars">
                  {r.origens.map((o) => (
                    <div key={o.origem} className="bars__row">
                      <span>{LEAD_ORIGIN_LABELS[o.origem]}</span>
                      <div className="bars__track">
                        <div className="bars__fill" style={{ width: `${(o.clientes / maxOrig) * 100}%` }} />
                      </div>
                      <strong>
                        {o.clientes} · {o.vendas} v.
                      </strong>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
